import useI18n from '@/hooks/useI18n';
import React, { useContext } from 'react';
import CustomChart from '../CustomChart';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@/context/UserContext';
import { formatCount, formatMoney } from '@/utils/numberUtils';
import { ROUTES } from '@/routes/constants';

const TopInfluencerComponent = ({ topInfluencers, filter }: any) => {
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { isDummyUser } = userContext;
  const navigate = useNavigate();

  const scaleSeriesToTotal = (data: number[], targetTotal: number) => {
    const sum = data.reduce((s, v) => s + v, 0);
    if (!sum) return data;

    const factor = targetTotal / sum;
    return data.map((v) => Math.round(v * factor));
  };
  const scaleSeriesToMax = (data: number[], targetMax: number) => {
    const currentMax = Math.max(...data);
    if (!currentMax) return data;

    const factor = targetMax / currentMax;
    return data.map((v) => Math.round(v * factor));
  };

  const generateInfluencerZigZag = (
    length: number,
    base: number,
    growth = 1.15,
    pattern = [1, 1.3, 0.85, 1.6, 1.1, 1.8, 1.25],
  ) => {
    return Array.from({ length }).map((_, index) => {
      const trend = base * Math.pow(growth, index);
      const factor = pattern[index % pattern.length];
      return Math.round(trend * factor);
    });
  };

  const getDummyTopInfluencerData = () => {
    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // ---------- VIEWS (≈ 3.1M total) ----------
    let viewsData = generateInfluencerZigZag(labels.length, 80_000, 1.18);

    viewsData = scaleSeriesToTotal(viewsData, 3_100_000);

    // ---------- LIKES (HIGH & CORRELATED) ----------
    let likesData = viewsData.map((view, index) => {
      // base engagement ~5–8%
      const engagementRate = [0.055, 0.058, 0.06, 0.075, 0.07, 0.082, 0.085];
      return Math.round(view * engagementRate[index]);
    });

    // 🎯 Force visual peak to ~257K
    likesData = scaleSeriesToMax(likesData, 257_000);

    return {
      analysis: {
        views: {
          labels,
          data: viewsData,
        },
        likes: {
          labels,
          data: likesData,
        },
      },
      user: {
        firstName: 'Demo',
        lastName: 'Creator',
        profileImage: '/assets/profileImg.svg',
        campaignCount: 1,
        postCount: 1,
        totalBuy: 180.0,
        socialScore: { totalScore: 920 },
      },
      platformData: {
        instagram: {
          username: 'demo_creator',
          followers_count: 124000,
        },
        tiktok: {
          username: 'demo.creator',
          follower_count: 88000,
        },
        youtube: {
          username: 'demoCreatorYT',
          followers_count: 42000,
        },
      },
    };
  };

  const resolvedInfluencer = isDummyUser
    ? getDummyTopInfluencerData()
    : topInfluencers;

  const { t } = useI18n();

  // The tooltip's "+X% from previous <word>" line must match the bucket the
  // backend returns. The backend now reports `granularity` directly (day / week
  // / month / quarter) so this is correct for custom ranges too — not just the
  // presets. Fall back to the preset mapping if granularity is absent (older
  // API / dummy data).
  const comparisonLabel =
    resolvedInfluencer?.analysis?.granularity ??
    (filter === 'last-week'
      ? 'day'
      : filter === 'last-month'
        ? 'week'
        : 'month');

  const chartSeries = [
    {
      name: String(t('userManagement.actions.view')),
      data: resolvedInfluencer?.analysis?.views?.data,
    },
    {
      name: String(t('common.like')),
      data: resolvedInfluencer?.analysis?.likes?.data,
    },
  ];

  const platformData = resolvedInfluencer?.platformData || {};

  const socialMediaData = [
    {
      key: 'instagram',
      platformIcon: '/assets/instagram.svg',
      username: platformData?.instagram?.username || null,
      followers: platformData?.instagram?.followers_count || null,
    },
    {
      key: 'tiktok',
      platformIcon: '/assets/logos_tiktok-icon.svg',
      username: platformData?.tiktok?.username || null,
      followers: platformData?.tiktok?.follower_count || null,
    },
  ].filter(Boolean);

  const postTooltipData = [
    String(t('userManagement.actions.view')),
    String(t('common.like')),
  ];
  const viewData = resolvedInfluencer?.analysis?.views?.data || [];
  const likeData = resolvedInfluencer?.analysis?.likes?.data || [];
  const categories: string[] =
    resolvedInfluencer?.analysis?.views?.labels || [];

  const viewsMax = viewData.length > 0 ? Math.max(...viewData) : 0;
  const likesMax = likeData.length > 0 ? Math.max(...likeData) : 0;
  // Y-axis is scaled to whichever series has the higher peak — views almost
  // always dominates, but fall back to likes max if not.
  const maxValue = Math.max(viewsMax, likesMax);

  const totalViews: number =
    viewData.length > 0 ? Math.max(...viewData.map((v: number) => v || 0)) : 0;

  const creatorName = [
    topInfluencers?.user?.firstName,
    topInfluencers?.user?.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  const socialScore =
    topInfluencers?.user?.socialScore?.totalScore ??
    resolvedInfluencer?.user?.socialScore?.totalScore;

  const handleNavigateToCreator = () => {
    navigate(ROUTES.CREATOR_SUBMISSIONS, {
      state: { userId: topInfluencers?.user?.userId },
    });
  };

  return (
    <div className="font-header">
      {/* Header row: title left, creator name + score + arrow right */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-medium text-[#121111] opacity-90 leading-[17px]">
          {t('common.topInfluencer')}
        </h2>
        {(creatorName || isDummyUser) && (
          <div className="flex items-center gap-2">
            <span className="text-xl font-normal text-[#191B1C] leading-8">
              {isDummyUser ? 'Demo Creator' : creatorName}
            </span>
            {socialScore !== undefined && (
              <span className="bg-[#E6F8F0] text-[#00AE3E] text-xs font-medium px-2 py-1 rounded-full">
                {socialScore}/1000
              </span>
            )}
            <button
              onClick={handleNavigateToCreator}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-white hover:bg-[#F9F9F9] transition-colors"
            >
              <img
                src="/assets/chevron-right.svg"
                alt="view creator"
                className="w-6 h-6"
              />
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-[16px] items-stretch">
        <div className="border border-[#E5E9EB] rounded-3xl w-3/5 p-[24px] flex flex-col gap-[32px] bg-white shadow-[0px_21px_94px_rgba(0,0,0,0.03)]">
          <div className="flex justify-between">
            <div>
              <div className="text-[#9B919D] text-xs font-normal mb-1">
                {t('common.totalViews')}
              </div>
              <div className="text-2xl font-medium leading-none text-[#292828]">
                {formatCount(totalViews)}
              </div>
            </div>
            {/* View + Like legend in a single row */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-1">
                <div className="bg-[#0FAF62] w-[12px] h-[12px] rounded-full" />
                <span className="text-[#9B919D] text-xs font-normal">
                  {t('userManagement.actions.view')}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <div
                  className="w-[12px] h-[12px] rounded-full"
                  style={{ backgroundColor: '#F3C6E2' }}
                />
                <span className="text-[#9B919D] text-xs font-normal">
                  {t('common.like')}
                </span>
              </div>
            </div>
          </div>
          <CustomChart
            chartSeries={chartSeries}
            categories={categories}
            TooltipData={postTooltipData}
            maXValue={maxValue === 10 ? 1000000 : maxValue}
            rotateLabels={false}
            height={284}
            yAxisFormatter={(value: number) => formatCount(value)}
            colors={['#0FAF62', '#F3C6E2']}
            variant="creatorInsights"
            changeComparisonLabel={comparisonLabel}
          />
        </div>

        <div className="border border-[#E5E9EB] rounded-3xl w-2/5 p-[24px] flex flex-col gap-[40px] bg-white">
          <div className="flex flex-1 justify-center items-center gap-8 py-8 px-6 bg-[#F9F9F9] rounded-2xl">
            {socialMediaData.map((item: any, index: number) => (
              <React.Fragment key={item.key}>
                <div className="flex flex-col items-center gap-2 min-w-0">
                  <img
                    src={item.platformIcon}
                    alt={item.key}
                    className="h-[24px]"
                  />

                  {/* Username */}
                  <div className="text-[#191B1C] text-xl font-normal leading-8 w-full text-center truncate">
                    {item.username || 'No Data'}
                  </div>

                  {/* Followers */}
                  <div className="bg-[#FFFFFF] text-[#9B919D] text-xs font-medium px-2 py-1 rounded-full max-w-[90px] truncate text-center">
                    {item.followers ? formatCount(item.followers) : '0'}
                  </div>
                </div>

                {index !== socialMediaData.length - 1 && (
                  <div className="w-[1px] h-[88px] bg-[#E5E7E8]" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Bottom Metrics Section */}
          <div className="p-[24px] flex gap-[20px] justify-between">
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex justify-center">
                <img
                  src={'/assets/archive.svg'}
                  alt={String(t('common.totalSubmission'))}
                  className="h-[24px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-2xl text-[#191B1C] text-center">
                  {resolvedInfluencer?.user?.postCount || 0}
                </div>
                <div className="text-xs text-[#9B919D] text-center capitalize">
                  {t('common.totalSubmission')}
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-2">
              <div className="flex justify-center">
                <img
                  src={'/assets/send.svg'}
                  alt={String(t('common.totalPosts'))}
                  className="h-[24px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-2xl text-[#191B1C] text-center">
                  {resolvedInfluencer?.user?.postCount || 0}
                </div>
                <div className="text-xs text-[#9B919D] text-center capitalize">
                  {t('common.totalPosts')}
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-2">
              <div className="flex justify-center">
                <img
                  src={'/assets/shopping-bag.svg'}
                  alt={String(t('common.totalBuy'))}
                  className="h-[24px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-2xl text-[#191B1C] text-center">
                  {formatMoney(resolvedInfluencer?.user?.totalBuy || 0)}
                </div>
                <div className="text-xs text-[#9B919D] text-center capitalize">
                  {t('common.totalBuy')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopInfluencerComponent;
